/**
 * Email API Client
 *
 * All calls include Authorization: Bearer <jwt_token> (in-memory, zero-knowledge).
 * Base URL from VITE_API_URL env var.
 *
 * Resilience features:
 * - Rate limit handling with exponential backoff (max 3 retries)
 * - Offline detection and queue
 * - 401 handling (logout + redirect)
 */

import type { NormalizedEmail, NormalizedThread } from '@signa/shared';
import { API_BASE } from './api-config';
import { useAuthStore } from '../stores/authStore';
import { useToastStore } from '../stores/toast-store';
import { queueMutation, replayMutations } from './mutation-queue';

interface CachedEmail {
  id: string;
  provider: 'gmail' | 'microsoft';
  threadId: string;
  subject: string;
  fromName: string;
  fromEmail: string;
  toRecipients: Array<{ name: string; email: string }>;
  ccRecipients?: Array<{ name: string; email: string }>;
  snippet: string;
  receivedAt: string;
  isRead: boolean;
  isStarred: boolean;
  isArchived?: boolean;
  hasAttachments: boolean;
  labels: string[];
}

interface EmailListResponse {
  emails: CachedEmail[];
  nextPageToken?: string;
}

interface EmailDetailResponse {
  email: NormalizedEmail;
}

interface SyncStatusResponse {
  lastSyncAt: string | null;
  emailCount: number;
  provider: 'gmail' | 'microsoft';
  status: 'idle' | 'syncing' | 'error';
  errorMessage?: string;
}

interface ProviderConnection {
  id: string;
  provider: 'gmail' | 'microsoft';
  email: string;
  scopes: string[];
  isActive: boolean;
  connectedAt: string;
  lastSyncAt: string | null;
  syncStatus?: 'idle' | 'syncing' | 'error';
  errorMessage?: string | null;
  accessTokenExpiresAt?: string;
}

interface ProviderConnectionsResponse {
  connections: ProviderConnection[];
}

type ApiError = {
  error: string;
  message: string;
  status: number;
};

const MAX_RETRIES = 3;
const BACKOFF_DELAYS = [1000, 2000, 4000]; // 1s, 2s, 4s

async function apiFetchWithRetry<T>(
  path: string,
  options?: RequestInit & { params?: Record<string, string | number | undefined> }
): Promise<T> {
  const token = useAuthStore.getState().token;
  const { params, ...fetchOptions } = options ?? {};

  let url = `${API_BASE}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string> ?? {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    // Offline check before each attempt
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      // Queue this mutation for later replay
      queueMutation({ url, method: (options?.method ?? 'GET') as string, body: options?.body as string | undefined });
      throw new Error('You are offline. Changes will sync when reconnected.');
    }

    const res = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (res.status === 401) {
      useAuthStore.getState().logout();
      throw new Error('Unauthorized - please log in again');
    }

    if (res.status === 429) {
      const retryAfter = res.headers.get('Retry-After');
      let waitMs = BACKOFF_DELAYS[Math.min(attempt, BACKOFF_DELAYS.length - 1)] ?? 4000;
      if (retryAfter) {
        const retrySec = parseInt(retryAfter, 10);
        if (!isNaN(retrySec)) {
          waitMs = retrySec * 1000;
        }
      }

      if (attempt < MAX_RETRIES) {
        const toastStore = useToastStore.getState();
        const toastId = `ratelimit-${Date.now()}`;
        toastStore.addToast({
          message: `Rate limited, retrying in ${Math.round(waitMs / 1000)}s...`,
          type: 'warning',
          countdown: Math.round(waitMs / 1000),
        });
        await new Promise((resolve) => setTimeout(resolve, waitMs));
        toastStore.removeToast(toastId);
        continue;
      } else {
        throw new Error('Rate limited - please try again later');
      }
    }

    if (res.status >= 500) {
      if (attempt < MAX_RETRIES) {
        const delay = BACKOFF_DELAYS[Math.min(attempt, BACKOFF_DELAYS.length - 1)];
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }
      throw new Error('Server error - please try again');
    }

    const data = (await res.json()) as unknown;
    if (res.status >= 400) {
      const err = data as { message?: string; error?: string };
      throw new Error(err.message ?? err.error ?? 'Request failed');
    }

    return data as T;
  }

  throw lastError ?? new Error('Request failed after retries');
}

// Re-export for direct use
async function apiFetch<T>(
  path: string,
  options?: RequestInit & { params?: Record<string, string | number | undefined> }
): Promise<T> {
  return apiFetchWithRetry<T>(path, options);
}

export const emailApi = {
  /**
   * GET /emails - list cached emails (no provider API call).
   * Returns emails from D1 cache ordered by received_at DESC.
   */
  list(
    provider: 'gmail' | 'microsoft',
    params?: {
      threadId?: string;
      pageSize?: number;
      pageToken?: string;
      archived?: boolean;
    }
  ): Promise<EmailListResponse> {
    return apiFetch<EmailListResponse>('/emails', {
      params: {
        provider,
        threadId: params?.threadId,
        pageSize: params?.pageSize,
        pageToken: params?.pageToken,
        archived: params?.archived === undefined ? undefined : params.archived ? 'true' : 'false',
      },
    });
  },

  /**
   * GET /emails/:id - get single email (decrypted on demand by API).
   */
  get(emailId: string): Promise<EmailDetailResponse> {
    return apiFetch<EmailDetailResponse>(`/emails/${emailId}`);
  },

  /**
   * GET /emails/threads/:threadId - get full thread assembled from cached emails.
   */
  getThread(threadId: string): Promise<{ thread: NormalizedThread }> {
    return apiFetch<{ thread: NormalizedThread }>(`/emails/threads/${threadId}`);
  },

  /**
   * POST /emails/sync - trigger sync.
   */
  sync(
    provider: 'gmail' | 'microsoft',
    strategy: 'full' | 'delta' | 'historical',
    days?: number
  ): Promise<{ status: string; result?: unknown }> {
    return apiFetch<{ status: string; result?: unknown }>('/emails/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, strategy, days }),
    });
  },

  /**
   * GET /emails/sync-status - get sync status for a provider.
   */
  getSyncStatus(provider: 'gmail' | 'microsoft'): Promise<SyncStatusResponse> {
    return apiFetch<SyncStatusResponse>('/emails/sync-status', {
      params: { provider },
    });
  },

  /**
   * DELETE /emails/:id - move to provider trash, then remove from cache.
   */
  delete(emailId: string): Promise<{ status: 'trashed'; emailId: string }> {
    return apiFetch<{ status: 'trashed'; emailId: string }>(`/emails/${emailId}`, { method: 'DELETE' });
  },

  /**
   * PATCH /emails/:id - update provider-backed email properties.
   */
  update(
    emailId: string,
    updates: { isRead?: boolean; isStarred?: boolean }
  ): Promise<{ status: 'updated'; emailId: string; updates: { isRead?: boolean; isStarred?: boolean } }> {
    return apiFetch<{ status: 'updated'; emailId: string; updates: { isRead?: boolean; isStarred?: boolean } }>(`/emails/${emailId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
  },

  /**
   * POST /emails/send - send a new email.
   */
  send(draft: {
    provider: 'gmail' | 'microsoft';
    to: Array<{ name?: string; email: string }>;
    cc?: Array<{ name?: string; email: string }>;
    bcc?: Array<{ name?: string; email: string }>;
    subject: string;
    bodyText: string;
    bodyHtml?: string;
  }): Promise<{ messageId: string }> {
    return apiFetch<{ messageId: string }>('/emails/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft),
    });
  },

  /**
   * POST /emails/threads/:threadId/reply - reply to a thread.
   */
  reply(threadId: string, draft: {
    provider: 'gmail' | 'microsoft';
    bodyText: string;
    bodyHtml?: string;
    cc?: Array<{ name?: string; email: string }>;
    bcc?: Array<{ name?: string; email: string }>;
  }): Promise<{ messageId: string }> {
    return apiFetch<{ messageId: string }>(`/emails/threads/${threadId}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft),
    });
  },

  /**
   * PATCH /emails/:id/archive - archive an email provider-side and in cache.
   */
  archive(emailId: string): Promise<{ status: 'archived'; emailId: string }> {
    return apiFetch<{ status: 'archived'; emailId: string }>(`/emails/${emailId}/archive`, {
      method: 'PATCH',
    });
  },
};

export const providerConnectionsApi = {
  /**
   * GET /provider-connections - list all connections for current user.
   */
  list(): Promise<ProviderConnectionsResponse> {
    return apiFetch<ProviderConnectionsResponse>('/provider-connections');
  },

  /**
   * DELETE /provider-connections/:id - revoke tokens and delete connection.
   */
  disconnect(connectionId: string): Promise<{ success: boolean; warning?: string }> {
    return apiFetch<{ success: boolean; warning?: string }>(
      `/provider-connections/${connectionId}`,
      { method: 'DELETE' }
    );
  },

  /**
   * Initiate OAuth flow for a provider.
   * Returns the OAuth URL to redirect to.
   */
  initiateOAuth(provider: 'gmail' | 'microsoft'): Promise<{ url: string }> {
    return apiFetch<{ url: string }>(`/oauth/${provider}`, {
      redirect: 'manual',
    }).catch(() => {
      // If redirect: manual returns no Location header, fall back to direct URL
      const url =
        provider === 'gmail'
          ? `${API_BASE}/oauth/gmail`
          : `${API_BASE}/oauth/microsoft`;
      return { url };
    });
  },

  /**
   * Exchange OAuth callback code for connection.
   */
  handleOAuthCallback(
    provider: 'gmail' | 'microsoft',
    code: string,
    state: string
  ): Promise<{ success: boolean }> {
    return apiFetch<{ success: boolean }>(`/oauth/${provider}/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state }),
    });
  },
};
